import { ArrowDown, ArrowUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type FlowFieldOptionRowLabels = {
  optionLabel: string;
  moveOptionUp: string;
  moveOptionDown: string;
  removeOption: string;
  optionSyncKeyPlaceholder: string;
  optionSyncKeyAria: string;
};

/** What the option rows edit — switched for all rows at once by the dialog. */
export type OptionEditMode = "label" | "syncKey";

type FlowFieldOptionRowProps = {
  mode: OptionEditMode;
  index: number;
  count: number;
  label: string;
  syncKey: string;
  /** Another option of this field already uses the same key. */
  syncKeyTaken: boolean;
  disabled: boolean;
  labels: FlowFieldOptionRowLabels;
  onLabelChange: (label: string) => void;
  onSyncKeyChange: (syncKey: string) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
};

/**
 * One option in the flow field dialog: reorder arrows, the edited value and
 * remove. In sync-key mode the label stays visible (read-only) next to the key
 * so the designer can see which option they are keying.
 */
export function FlowFieldOptionRow({
  mode,
  index,
  count,
  label,
  syncKey,
  syncKeyTaken,
  disabled,
  labels,
  onLabelChange,
  onSyncKeyChange,
  onMove,
  onRemove,
}: FlowFieldOptionRowProps) {
  const placeholder = `${labels.optionLabel} ${index + 1}`;

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={labels.moveOptionUp}
          onClick={() => onMove(-1)}
          disabled={index === 0 || disabled}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={labels.moveOptionDown}
          onClick={() => onMove(1)}
          disabled={index === count - 1 || disabled}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>
      {mode === "label" ? (
        <Input
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
          disabled={disabled}
        />
      ) : (
        <>
          <span
            className={cn(
              "w-2/5 shrink-0 truncate text-sm",
              label.trim() ? "text-muted-foreground" : "italic text-muted-foreground/60",
            )}
            title={label}
          >
            {label.trim() || placeholder}
          </span>
          <Input
            value={syncKey}
            onChange={(e) => onSyncKeyChange(e.target.value)}
            placeholder={labels.optionSyncKeyPlaceholder}
            aria-label={`${label.trim() || placeholder} — ${labels.optionSyncKeyAria}`}
            className={cn("flex-1 font-mono", syncKeyTaken && "border-destructive")}
            aria-invalid={syncKeyTaken}
            autoComplete="off"
            spellCheck={false}
            disabled={disabled}
          />
        </>
      )}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={labels.removeOption}
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
